/**
 * Accessible toast notification system using react-hot-toast
 * Implements WCAG-compliant notifications with ARIA live regions
 *
 * IMPORTANT: This replaces all alert() and window.confirm() usage
 * with accessible, non-blocking toast notifications
 */

import toast from 'react-hot-toast';

/**
 * Default toast configuration with accessibility enhancements
 */
const defaultToastOptions = {
  duration: 4000,
  position: 'top-center',
  // ARIA announcements handled automatically by react-hot-toast
  ariaProps: {
    role: 'status',
    'aria-live': 'polite',
  },
  style: {
    borderRadius: '12px',
    background: '#ffffff',
    color: '#1e293b',
    padding: '16px 20px',
    fontSize: '15px',
    fontWeight: '500',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    maxWidth: '500px',
  },
};

/**
 * Success notification
 * @param {string} message - Message to display
 * @param {object} options - Additional toast options
 */
export const showSuccess = (message, options = {}) => {
  return toast.success(message, {
    ...defaultToastOptions,
    ...options,
    icon: '✓',
    style: {
      ...defaultToastOptions.style,
      borderLeft: '4px solid #10b981',
      ...options.style,
    },
  });
};

/**
 * Error notification
 * @param {string} message - Error message to display
 * @param {object} options - Additional toast options
 */
export const showError = (message, options = {}) => {
  return toast.error(message, {
    ...defaultToastOptions,
    duration: 6000, // Errors stay longer
    ...options,
    icon: '✕',
    ariaProps: {
      role: 'alert', // Use alert for errors (assertive)
      'aria-live': 'assertive',
    },
    style: {
      ...defaultToastOptions.style,
      borderLeft: '4px solid #ef4444',
      ...options.style,
    },
  });
};

/**
 * Warning notification
 * @param {string} message - Warning message to display
 * @param {object} options - Additional toast options
 */
export const showWarning = (message, options = {}) => {
  return toast(message, {
    ...defaultToastOptions,
    ...options,
    icon: '⚠️',
    style: {
      ...defaultToastOptions.style,
      borderLeft: '4px solid #f59e0b',
      ...options.style,
    },
  });
};

/**
 * Info notification
 * @param {string} message - Info message to display
 * @param {object} options - Additional toast options
 */
export const showInfo = (message, options = {}) => {
  return toast(message, {
    ...defaultToastOptions,
    ...options,
    icon: 'ℹ️',
    style: {
      ...defaultToastOptions.style,
      borderLeft: '4px solid #3b82f6',
      ...options.style,
    },
  });
};

/**
 * Confirmation dialog replacement (accessible alternative to window.confirm)
 * Returns a Promise that resolves to true/false based on user choice
 *
 * NOTE: This function now triggers a global confirmation dialog event.
 * The actual ConfirmDialog component should be mounted at the root level
 * and listen to this event via the useConfirm hook.
 *
 * For now, keeping the toast-based implementation for backward compatibility.
 * Components should migrate to using the useConfirm hook directly for better UX.
 *
 * @param {string} message - Confirmation message
 * @param {object} options - Customization options
 * @returns {Promise<boolean>} - Resolves to true if confirmed, false if cancelled
 */
export const showConfirm = (message, options = {}) => {
  return new Promise((resolve) => {
    const {
      confirmText = 'אישור',
      cancelText = 'ביטול',
      confirmButtonStyle = {},
      cancelButtonStyle = {},
      ...toastOptions
    } = options;

    toast(
      (t) => (
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              marginBottom: '16px',
              fontSize: '16px',
              fontWeight: '600',
              color: '#1e293b',
            }}
          >
            {message}
          </div>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
            }}
          >
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(true);
              }}
              style={{
                padding: '10px 24px',
                backgroundColor: '#1324a1',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                ...confirmButtonStyle,
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#0f1d7a';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1324a1';
              }}
              aria-label={confirmText}
            >
              {confirmText}
            </button>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                resolve(false);
              }}
              style={{
                padding: '10px 24px',
                backgroundColor: '#ffffff',
                color: '#1324a1',
                border: '2px solid #1324a1',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                ...cancelButtonStyle,
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#0f1d7a';
                e.target.style.color = '#0f1d7a';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#1324a1';
                e.target.style.color = '#1324a1';
              }}
              aria-label={cancelText}
            >
              {cancelText}
            </button>
          </div>
        </div>
      ),
      {
        ...defaultToastOptions,
        duration: Infinity, // Stay until user responds
        ...toastOptions,
        ariaProps: {
          role: 'alertdialog',
          'aria-live': 'assertive',
        },
        style: {
          ...defaultToastOptions.style,
          borderLeft: '4px solid #f59e0b',
          minWidth: '400px',
          ...toastOptions.style,
        },
      }
    );
  });
};

/**
 * Loading notification with progress
 * @param {string} message - Loading message
 * @param {Promise} promise - Promise to track
 * @returns {Promise} - Original promise
 */
export const showLoading = (message, promise) => {
  return toast.promise(
    promise,
    {
      loading: message,
      success: (data) => data?.message || 'הושלם בהצלחה!',
      error: (err) => err?.message || 'אירעה שגיאה',
    },
    {
      ...defaultToastOptions,
      loading: {
        icon: '⏳',
        style: {
          ...defaultToastOptions.style,
          borderLeft: '4px solid #6ab3eb',
        },
      },
      success: {
        icon: '✓',
        duration: 3000,
        style: {
          ...defaultToastOptions.style,
          borderLeft: '4px solid #10b981',
        },
      },
      error: {
        icon: '✕',
        duration: 5000,
        style: {
          ...defaultToastOptions.style,
          borderLeft: '4px solid #ef4444',
        },
        ariaProps: {
          role: 'alert',
          'aria-live': 'assertive',
        },
      },
    }
  );
};

/**
 * Dismiss all toasts
 */
export const dismissAll = () => {
  toast.dismiss();
};

/**
 * Dismiss specific toast by ID
 * @param {string} toastId - ID of toast to dismiss
 */
export const dismiss = (toastId) => {
  toast.dismiss(toastId);
};

// Re-export toast for custom usage
export { toast };

// Default export with all methods
export default {
  success: showSuccess,
  error: showError,
  warning: showWarning,
  info: showInfo,
  confirm: showConfirm,
  loading: showLoading,
  dismiss,
  dismissAll,
};
